import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { ChecklistGroup, ChecklistItem } from '../../../interfaces/checklist.model';
import { CommonModule } from '@angular/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { ButtonModule } from 'primeng/button';
import { ButtonComponent } from '@ppl/ui';
import { ModalComponent } from '@ppl/ui';
import { EditpdfComponent } from '../../editpdf/editpdf.component';
import { CommPreferenceComponent } from '../comm-preference/comm-preference.component';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { RADIO_YES_NO_OPTIONS } from '../../../shared/constants/static-data.constants';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { FormChecklistComponent } from '../form-checklist/form-checklist.component';
import { DropdownSelectComponent } from '@ppl/ui';

import { FormsModule } from '@angular/forms';
import { NzCheckboxComponent } from 'ng-zorro-antd/checkbox';

@Component({
  selector: 'ppl-checklist-view',
  standalone: true,
  imports: [NzCollapseModule,
    CommonModule, ButtonModule,
    NzToolTipModule,
    NzRadioModule,
    NzButtonModule,
    ButtonComponent,
    NzAlertModule,
    NzInputModule,
    NzDatePickerModule,
    DropdownSelectComponent,
    NzSelectModule,
    NzCheckboxComponent,
    FormsModule],
  templateUrl: './checklist-view.component.html',
  styleUrl: './checklist-view.component.css'
})
export class ChecklistViewComponent implements OnInit, OnChanges {
  @Input() checklistData: ChecklistGroup[] = [];
  @Input() showNoRecordsMesssage = true;
  @Input() isSelf = true;
  @Input() isEditable = true;
  @Output() viewPDF = new EventEmitter<ChecklistItem>();
  message: any;

  radioOptions = RADIO_YES_NO_OPTIONS;
  date = '';

  // Map to store all checklist items by ID for quick lookup
  private checklistItemsMap = new Map<number, ChecklistItem>();

  ngOnInit(): void {
    if (this.isSelf) {
      this.message = "No Self checklist available for you.";
    } else {
      this.message = "Checklist is not applicable to your association with the participant.";
    }

    // make all checklist groups as expanded by default
    this.checklistData.forEach(group => {
      group.isActive = true;
      group.children?.forEach(category => {
        category.isActive = true;
        category.children?.forEach(subCategory => {
          subCategory.isActive = true;
        });
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When checklistData changes, rebuild the map and setup conditional mandatory logic
    if (changes['checklistData'] && this.checklistData && this.checklistData.length > 0) {
      this.buildChecklistItemsMap();
      this.setupCheckboxFiltering();
      this.setupConditionalMandatory();
    }
  }

  isPanelActive(group: ChecklistGroup): boolean {
    return group.isActive;
  }

  onPanelHeaderKeyPress(group: ChecklistGroup) {
    group.isActive = !group.isActive;

    if (group.isActive) {
      setTimeout(() => {
        const panelEl = document.querySelector(
          `[data-checklist-id="${group.checklistName}"]`
        );
        const focusTarget = panelEl?.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement;
        focusTarget?.focus();
      }, 100);
    }
  }

  onChange(event: any, item: ChecklistItem) {
    item.fromDate = event[0] ? event[0].toISOString().split('T')[0] : '';
    item.toDate = event[1] ? event[1].toISOString().split('T')[0] : '';
  }

  handleCheckListClick(form: ChecklistItem) {
    this.viewPDF.emit(form);
  }

  /**
   * Build a map of all checklist items by their ID for quick lookup
   */
  private buildChecklistItemsMap(): void {
    this.checklistItemsMap.clear();
    this.traverseChecklistGroups(this.checklistData, (item) => {
      if (!this.isQuestionMarkPresent(item?.question)) {
        item.question = item.question.trimEnd().concat('?');
      }
      this.checklistItemsMap.set(item.checklistItemId, item);
    });
  }

  /**
   * Traverse all checklist groups and apply a callback to each item
   */
  private traverseChecklistGroups(groups: ChecklistGroup[], callback: (item: ChecklistItem) => void): void {
    groups.forEach(group => {
      // Process items in current group
      group.items?.forEach(item => callback(item));

      // Recursively process children
      if (group.children && group.children.length > 0) {
        this.traverseChecklistGroups(group.children, callback);
      }
    });
  }

  /**
   * Set up conditional mandatory logic for all items
   */
  private setupConditionalMandatory(): void {
    this.traverseChecklistGroups(this.checklistData, (item) => {
      if (item.conditionalMandatory) {
        // Initialize the conditional mandatory state
        this.updateConditionalMandatory(item);
      }
    });
    this.setupServiceCredentialVisibility();
    this.recalculateGroupCounts();
  }

  private static readonly SERVICE_CODE_PATTERN = /(\d{3})\s*[-:]/;
  private static readonly SERVICE_CREDENTIAL_SUBCATEGORY = 'Service Credentials';

  /**
   * For credential number and expiration date items under Service Credentials,
   * derive visibility from the parent dropdown (same service code) by matching service code.
   * These items should only be visible when the parent dropdown value is "Yes".
   */
  private setupServiceCredentialVisibility(): void {
    // Build a map of service code -> parent dropdown item within Service Credentials
    const certDropdownMap = new Map<string, ChecklistItem>();
    this.traverseChecklistGroups(this.checklistData, (item) => {
      if (item.subCategory !== ChecklistViewComponent.SERVICE_CREDENTIAL_SUBCATEGORY) return;
      if (item.fieldType !== 'dropdown') return;
      const match = item.question?.match(ChecklistViewComponent.SERVICE_CODE_PATTERN);
      if (match) {
        certDropdownMap.set(match[1], item);
      }
    });

    // Link credential/expiration items to their parent dropdown
    this.traverseChecklistGroups(this.checklistData, (item) => {
      if (item.subCategory !== ChecklistViewComponent.SERVICE_CREDENTIAL_SUBCATEGORY) return;
      if (item.conditionalMandatory || item.hide) return;
      const isCredentialOrExpiration = item.question?.includes('Credential Number') || item.question?.includes('Expiration Date');
      if (!isCredentialOrExpiration) return;

      const codeMatch = item.question?.match(ChecklistViewComponent.SERVICE_CODE_PATTERN);
      if (!codeMatch) return;

      const parentDropdown = certDropdownMap.get(codeMatch[1]);
      if (parentDropdown) {
        item.hide = String(parentDropdown.value || '').trim() !== 'Yes';
      }
    });
  }

  /**
   * Update credential/expiration visibility when a Service Credentials dropdown changes
   */
  private updateServiceCredentialVisibility(changedItem: ChecklistItem): void {
    if (changedItem.subCategory !== ChecklistViewComponent.SERVICE_CREDENTIAL_SUBCATEGORY) return;
    if (changedItem.fieldType !== 'dropdown') return;

    const codeMatch = changedItem.question?.match(ChecklistViewComponent.SERVICE_CODE_PATTERN);
    if (!codeMatch) return;

    const serviceCode = codeMatch[1];
    this.traverseChecklistGroups(this.checklistData, (item) => {
      if (item.subCategory !== ChecklistViewComponent.SERVICE_CREDENTIAL_SUBCATEGORY) return;
      const isCredentialOrExpiration = item.question?.includes('Credential Number') || item.question?.includes('Expiration Date');
      if (!isCredentialOrExpiration) return;

      const itemCodeMatch = item.question?.match(ChecklistViewComponent.SERVICE_CODE_PATTERN);
      if (itemCodeMatch?.[1] !== serviceCode) return;

      item.hide = String(changedItem.value || '').trim() !== 'Yes';
    });
  }

  /**
   * Update conditional mandatory state for an item based on parent field value(s)
   * Supports both single dependency (legacy) and multiple dependencies (new)
   * Note: Only updates the required flag, does not disable fields
   */
  private updateConditionalMandatory(item: ChecklistItem): void {
    if (!item.conditionalMandatory) return;

    let conditionMet = false;

    // Check if using new multi-dependency format
    if (item.conditionalMandatory.dependencies && item.conditionalMandatory.dependencies.length > 0) {
      const operator = item.conditionalMandatory.operator || 'AND';
      const results = item.conditionalMandatory.dependencies.map(dep => {
        const parentItem = this.checklistItemsMap.get(dep.dependsOnChecklistItemId);
        if (!parentItem) return false;

        const parentValue = String(parentItem.value || '').trim();
        const expectedValue = String(dep.expectedValue || '').trim();
        return parentValue === expectedValue;
      });

      // Apply operator logic
      if (operator === 'OR') {
        conditionMet = results.some(r => r); // At least one must be true
      } else {
        conditionMet = results.every(r => r); // All must be true
      }
    }
    // Legacy single dependency format (backward compatible)
    else if (item.conditionalMandatory.dependsOnChecklistItemId) {
      const parentItem = this.checklistItemsMap.get(item.conditionalMandatory.dependsOnChecklistItemId);
      if (parentItem) {
        const parentValue = String(parentItem.value || '').trim();
        const expectedValue = String(item.conditionalMandatory.expectedValue || '').trim();
        conditionMet = parentValue === expectedValue;
      }
    }

    item.isConditionallyMandatory = conditionMet && item.conditionalMandatory.mandatory;
    // Only gate visibility on conditionMet when there's an actual dependency to evaluate.
    // A conditionalMandatory block with no dependency (e.g. one carrying only enableForEdit)
    // shouldn't hide the item — that's a "no condition" signal, not "condition not met".
    const hasDependency = (item.conditionalMandatory?.dependencies?.length ?? 0) > 0
      || item.conditionalMandatory?.dependsOnChecklistItemId != null;
    item.hide = hasDependency ? !conditionMet : false;
  }

  /**
   * Handle value changes for any field
   * This triggers conditional mandatory updates for dependent fields
   * Supports both single dependency (legacy) and multiple dependencies (new)
   */
  onValueChange(item: ChecklistItem): void {
    // Find all items that depend on this item
    let hasDependents = false;
    this.traverseChecklistGroups(this.checklistData, (dependentItem) => {
      if (!dependentItem.conditionalMandatory) return;

      // Check legacy single dependency format
      const hasLegacyDependency = dependentItem.conditionalMandatory.dependsOnChecklistItemId === item.checklistItemId;

      // Check new multi-dependency format
      const hasMultiDependency = dependentItem.conditionalMandatory.dependencies?.some(
        dep => dep.dependsOnChecklistItemId === item.checklistItemId
      );

      if (hasLegacyDependency || hasMultiDependency) {
        this.updateConditionalMandatory(dependentItem);
        hasDependents = true;
      }
    });

    // Update credential/expiration visibility when a certification dropdown changes
    this.updateServiceCredentialVisibility(item);

    // Recalculate whenever mandatory state or visibility may have shifted. updateServiceCredentialVisibility
    // can flip `hide` on items that have no conditionalMandatory pointing back at this one, so gating purely
    // on hasDependents left the counts and pending lists stale in that case.
    if (hasDependents || this.isServiceCredentialDropdown(item)) {
      this.recalculateGroupCounts();
    }
  }

  private isServiceCredentialDropdown(item: ChecklistItem): boolean {
    return item.subCategory === ChecklistViewComponent.SERVICE_CREDENTIAL_SUBCATEGORY
      && item.fieldType === 'dropdown';
  }

  /**
   * Recalculate totalItems, totalItemsCompleted, and isCompleted for all groups bottom-up.
   * An item is effectively mandatory if item.mandatory === true OR item.isConditionallyMandatory === true.
   */
  private recalculateGroupCounts(): void {
    this.checklistData.forEach(association => {
      // Association level (top) — children are category groups
      association.children?.forEach(category => {
        // Category level — children are subCategory groups, items are sameAsCategory items
        category.children?.forEach(subCategory => {
          // SubCategory level — leaf items
          const mandatoryItems = (subCategory.items || []).filter(i => !i.hide && (i.mandatory || i.isConditionallyMandatory));
          const mandatoryCompleted = mandatoryItems.filter(i => i.checkListStatus === 'C').length;
          subCategory.totalItems = mandatoryItems.length;
          subCategory.totalItemsCompleted = mandatoryCompleted;
          subCategory.isCompleted = mandatoryCompleted === mandatoryItems.length;
        });

        // Category: sameAsCategory items are in category.items, subGroups are in category.children
        const mandatorySameAsCategory = (category.items || []).filter(i => !i.hide && (i.mandatory || i.isConditionallyMandatory));
        const subGroupsCompleted = (category.children || []).filter(c => c.isCompleted).length;
        const sameAsCategoryCompleted = mandatorySameAsCategory.filter(i => i.checkListStatus === 'C').length;

        category.totalItems = (category.children || []).length + mandatorySameAsCategory.length;
        category.totalItemsCompleted = subGroupsCompleted + sameAsCategoryCompleted;
        category.isCompleted = category.totalItemsCompleted === category.totalItems;
      });

      // Association: count completed category groups
      const categoryGroupsCompleted = (association.children || []).filter(c => c.isCompleted).length;
      association.totalItems = (association.children || []).length;
      association.totalItemsCompleted = categoryGroupsCompleted;
      association.isCompleted = categoryGroupsCompleted === (association.children || []).length;
    });
  }

  onCheckboxChange(checked: boolean, item: ChecklistItem, key: string): void {
    let values: string[] = [];

    if (typeof item.value === 'string' && item.value.trim() !== '') {
      values = item.value.split(',').map(v => v.trim());
    }

    if (checked) {
      if (!values.includes(key)) {
        values.push(key);
      }
    } else {
      values = values.filter(v => v !== key);
    }

    item.value = values.join(',');
    this.updateItemVisibilityForCheckbox(item);
    this.recalculateGroupCounts();
    this.onValueChange(item);
  }

  /**
   * Check if a field should be marked as required (for UI display)
   */
  isFieldRequired(item: ChecklistItem): boolean {
    return item.mandatory || (item.isConditionallyMandatory === true);
  }

  isItemComplete(item: ChecklistItem): boolean {
    if (this.isFieldRequired(item)) {
      return item.checkListStatus === 'C';
    }
    return item.checkListStatus === 'C' || (item.value != null && String(item.value).trim() !== '');
  }

  /**
   * A required item that has not been completed yet — flagged in the UI so the
   * user can tell "still to do" apart from "optional and left blank".
   */
  isPendingRequired(item: ChecklistItem): boolean {
    return this.isFieldRequired(item) && !this.isItemComplete(item);
  }

  isQuestionMarkPresent(questionText: string): boolean {
    if (questionText?.trimEnd().endsWith('?')) {
      return true;
    }

    return false;
  }

  /**
   * Set up checkbox-based visibility filtering.
   * When a checkbox item exists in a group, sibling items with matching service codes
   * are shown/hidden based on the checkbox's selected values.
   */
  private setupCheckboxFiltering(): void {
    this.traverseChecklistGroups(this.checklistData, (item) => {
      if (item.fieldType === 'checkbox' && item.lookupValue?.length > 0) {
        this.updateItemVisibilityForCheckbox(item);
      }
    });
    this.recalculateGroupCounts();
  }

  /**
   * Update visibility of sibling items based on checkbox selections.
   * Items whose questions contain a service code from the checkbox's lookup values
   * are shown only if that code is among the selected values.
   */
  private updateItemVisibilityForCheckbox(checkboxItem: ChecklistItem): void {
    const selectedValues = this.getCheckboxSelectedValues(checkboxItem);
    const serviceCodePattern = /\b(\d{3})\s*[-:]\s/;

    this.traverseChecklistGroups(this.checklistData, (item) => {
      if (item.checklistItemId === checkboxItem.checklistItemId) return;
      if (item.category !== checkboxItem.category || item.subCategory !== checkboxItem.subCategory) return;

      const match = item.question.match(serviceCodePattern);
      if (match) {
        const serviceCode = match[1];
        const isCheckboxOption = checkboxItem.lookupValue.some(lv => lv.key === serviceCode);
        if (isCheckboxOption) {
          const isSelected = selectedValues.includes(serviceCode);
          item.hide = !isSelected;

          // Auto-set unselected service dropdowns to "N/A", selected ones to "Yes"
          if (!isSelected && item.fieldType === 'dropdown') {
            item.value = 'N/A';
          } else if (isSelected && item.fieldType === 'dropdown' && (!item.value || item.value === 'N/A')) {
            item.value = 'Yes';
          }

          // Update conditional mandatory for dependents of this item
          this.onValueChange(item);
        }
      }
    });
  }

  private getCheckboxSelectedValues(item: ChecklistItem): string[] {
    if (!item.value || typeof item.value !== 'string' || item.value.trim() === '') {
      return [];
    }
    return item.value.split(',').map(v => v.trim());
  }
}
